import React, { useMemo } from "react";

/**
 * Theme-aligned TextAvatar (Indigo #4F46E5)
 * - Uses soft indigo surface + indigo text by default
 * - Looks good in tables/lists (no huge mb-3 unless requested)
 * - Supports "selected" + "muted" variants
 */
const TextAvatar = ({
  text = "",
  size = 44,
  fontSize = 14,
  variant = "indigo", // "indigo" | "muted" | "solid" | "danger"
  className = "",
  title,
}) => {
  const initials = useMemo(() => {
    const t = String(text || "").trim();
    if (!t) return "•";
    return t.length > 3 ? t.slice(0, 3).toUpperCase() : t.toUpperCase();
  }, [text]);

  const styles = useMemo(() => {
    const base = {
      width: typeof size === "number" ? `${size}px` : size,
      height: typeof size === "number" ? `${size}px` : size,
      fontSize: typeof fontSize === "number" ? `${fontSize}px` : fontSize,
      fontWeight: 900,
      letterSpacing: "0.02em",
      lineHeight: 1,
      userSelect: "none",
      border: "1px solid rgba(15,23,42,.08)",
      background: "rgba(15,23,42,.04)",
      color: "rgba(71,85,105,.95)",
    };

    if (variant === "solid") {
      base.background = "#4F46E5";
      base.color = "#fff";
      base.border = "1px solid rgba(79,70,229,.28)";
      base.boxShadow = "0 8px 18px rgba(79,70,229,.18)";
    }

    if (variant === "indigo") {
      base.background = "rgba(79,70,229,.10)";
      base.color = "#4F46E5";
      base.border = "1px solid rgba(79,70,229,.18)";
      base.boxShadow = "0 1px 0 rgba(15,23,42,.04)";
    }

    if (variant === "muted") {
      base.background = "rgba(148,163,184,.22)";
      base.color = "rgba(30,41,59,.92)";
      base.border = "1px solid rgba(148,163,184,.30)";
    }

    if (variant === "danger") {
      base.background = "rgba(225,29,72,.10)";
      base.color = "#E11D48";
      base.border = "1px solid rgba(225,29,72,.18)";
    }

    return base;
  }, [size, fontSize, variant]);

  return (
    <div
      className={`d-inline-flex align-items-center justify-content-center rounded-circle ${className}`}
      style={styles}
      title={title || (text ? String(text) : undefined)}
      aria-label={title || text || "avatar"}
    >
      {initials}
    </div>
  );
};

export default TextAvatar;
